import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { Button, ButtonProps, Icon } from '@column/column-ui-kit';

import { DocumentRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { downloadFile } from '~/util';

interface DownloadButtonProps extends ButtonProps {
  label: string;
  fileName: string;
  id?: string;
}

const StyledButton = styled(Button)`
  font-size: 13px;
  min-height: 32px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  white-space: nowrap;
  line-height: 1;

  svg {
    --icon-size: 14px;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

export const DownloadButton: React.FC<DownloadButtonProps> = ({ label, fileName, id, ...buttonProps }) => {
  const { addDangerNotification } = useNotificationStore();

  const [running, setRunning] = useState<boolean>(false);

  const handleDownload = useCallback(
    (docId?: string) => {
      if (!docId) {
        return;
      }

      setRunning(true);

      DocumentRepository.get(docId, { contentDisposition: `attachment; filename="${fileName}"` })
        .then((response) => downloadFile(response.url, fileName))
        .catch((error) =>
          addDangerNotification({
            content: error.message,
          })
        );
    },
    [fileName, addDangerNotification]
  );

  return (
    <StyledButton
      onClick={(e: React.MouseEvent) => {
        handleDownload(id);
        e.stopPropagation();
      }}
      isDisabled={!id}
      size="small"
      icon={<Icon.AnimationDownload running={running} onDone={() => setRunning(false)} />}
      {...buttonProps}
    >
      {label}
    </StyledButton>
  );
};
